import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRightIcon,
  BoltIcon,
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  ClockIcon,
  DevicePhoneMobileIcon,
  DocumentCheckIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  LockClosedIcon,
  PlusIcon,
  ShieldCheckIcon,
  SparklesIcon,
  StarIcon,
} from '@heroicons/react/24/outline';
import { PricingComparisonCards } from '../pricing/PricingComparisonSection';
import CompanyDisclosure from '../legal/CompanyDisclosure';
import { COMPANY } from '../../constants/company';
import ProductDemoStage from './ProductDemoStage';

function SectionHead({ eyebrow, title, subtitle, center = false }) {
  return (
    <div className={`hpv2-section-head${center ? ' hpv2-section-head--center' : ''}`}>
      {eyebrow ? <div className="hpv2-eyebrow">{eyebrow}</div> : null}
      <h2>{title}</h2>
      {subtitle ? <p>{subtitle}</p> : null}
    </div>
  );
}

export function HeroSection() {
  return (
    <section className="hpv2-hero" aria-labelledby="hpv2-hero-heading">
      <div className="hpv2-hero-glow" aria-hidden="true" />
      <div className="hpv2-container">
        <div className="hpv2-hero-copy hpv2-reveal">
          <p className="hpv2-pill">
            <span className="hpv2-pill-dot" aria-hidden="true" />
            UK dental consent · Clinician reviewed
          </p>
          <p className="hpv2-hero-brand">
            <span>SmileConsent</span>
          </p>
          <h1 id="hpv2-hero-heading">
            AI-assisted dental consent — drafted, reviewed, and{' '}
            <span className="hpv2-gradient-text">signed with a clear record.</span>
          </h1>
          <p className="hpv2-hero-lead">
            Select a patient, add treatments and costs, review the draft, then send for digital signature.
            Clinicians stay in control of the final wording.
          </p>
          <div className="hpv2-hero-ctas">
            <Link to="/register" className="hpv2-btn hpv2-btn--primary">
              Start your 14-day free trial
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
            </Link>
            <Link to="/book-demo" className="hpv2-btn hpv2-btn--ghost">
              Book a demo
            </Link>
          </div>
          <p className="hpv2-hero-note">
            No charge during your trial · Cancel anytime · No long-term contracts
          </p>
        </div>

        <div className="hpv2-reveal hpv2-reveal-delay-1">
          <ProductDemoStage />
        </div>
      </div>
    </section>
  );
}

export function TrustStrip() {
  const items = [
    { icon: ShieldCheckIcon, text: 'Built for UK dental consent workflows' },
    { icon: ClipboardDocumentCheckIcon, text: 'Clinician review before sending' },
    { icon: LockClosedIcon, text: 'GDPR-aware cloud records' },
    { icon: ClockIcon, text: '14-day free trial — cancel anytime' },
  ];

  return (
    <section className="hpv2-trust" aria-label="Trust signals">
      <div className="hpv2-container">
        <div className="hpv2-trust-row hpv2-reveal">
          {items.map(({ icon: Icon, text }) => (
            <div key={text} className="hpv2-trust-item">
              <Icon aria-hidden="true" />
              <span>{text}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function ContrastSection() {
  const oldWay = [
    'Printed forms in a drawer',
    'Did anyone get a signature?',
    'Chasing the patient by phone',
    'No clear record later',
  ];
  const newWay = [
    {
      title: 'One pathway for every consent',
      body: 'Patient, treatments, costs, and wording stay in the same journey.',
    },
    {
      title: 'A draft you edit before it leaves',
      body: 'AI assembles a starting point. Clinicians remain responsible for the final wording.',
    },
    {
      title: 'Signed from the conversation',
      body: 'Patients review and sign on their phone, in the practice or at home.',
    },
    {
      title: 'See what is still unsigned',
      body: 'The next morning, anything waiting for that day is on the dashboard.',
    },
  ];

  return (
    <section className="hpv2-section hpv2-contrast" id="old-way" aria-labelledby="hpv2-contrast-title">
      <div className="hpv2-container">
        <div className="hpv2-contrast-grid hpv2-reveal">
          <div className="hpv2-contrast-old">
            <h2 id="hpv2-contrast-title">The old way</h2>
            <p>Consent scattered across paper, inboxes, and memory</p>
            <ol>
              {oldWay.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ol>
          </div>
          <div className="hpv2-contrast-bolt" aria-hidden="true">
            <BoltIcon className="h-6 w-6" />
          </div>
          <div className="hpv2-contrast-new">
            <h2>The SmileConsent way</h2>
            <p>One pathway from draft to signature, answered the same day</p>
            <ul>
              {newWay.map((item) => (
                <li key={item.title}>
                  <strong>{item.title}</strong>
                  <span>{item.body}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

export function ProblemsSection() {
  const problems = [
    {
      title: 'Paper trails that do not hold up',
      body:
        'Printed forms and informal conversations leave gaps when a complaint, claim, or inspection asks what was explained and when.',
    },
    {
      title: 'Inconsistent wording across the team',
      body:
        'Different clinicians draft consent differently. Material risks and alternatives can be missed under time pressure.',
    },
    {
      title: 'Hard to prove what happened',
      body:
        'Without a clear digital pathway — draft, review, send, sign — practices struggle to reconstruct the consent journey later.',
    },
  ];

  return (
    <section className="hpv2-section" id="problems" aria-labelledby="hpv2-problems-title">
      <div className="hpv2-container">
        <div className="hpv2-reveal">
          <SectionHead
            eyebrow="The pressure"
            title="Dentists do not need more forms. They need a clearer consent pathway."
            subtitle="After Montgomery, clinicians must disclose material risks and reasonable alternatives. SmileConsent helps practices structure that conversation into a reviewable, signable record — without inventing compliance guarantees."
          />
        </div>
        <div className="hpv2-problems-grid">
          {problems.map((item, index) => (
            <article
              key={item.title}
              className={`hpv2-problem hpv2-reveal hpv2-reveal-delay-${index + 1}`}
            >
              <h3>{item.title}</h3>
              <p>{item.body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

export function WorkflowSection() {
  const steps = [
    {
      title: 'Choose the patient',
      body: 'Find the right person quickly, then start a consent journey tied to their record.',
    },
    {
      title: 'Add treatments & costs',
      body: 'Select procedures, capture NHS or private context, and include the cost conversation in the draft.',
    },
    {
      title: 'Review the AI draft',
      body: 'Edit wording before anything leaves the practice. Clinicians remain responsible for the final content.',
    },
    {
      title: 'Send & sign digitally',
      body: 'Patients review and sign from their device. The next morning, anything still unsigned for that day is waiting when you log in.',
    },
  ];

  return (
    <section className="hpv2-section hpv2-workflow" id="how-it-works" aria-labelledby="hpv2-workflow-title">
      <div className="hpv2-container">
        <div className="hpv2-reveal">
          <SectionHead
            center
            eyebrow="Product workflow"
            title="From chairside decision to signed consent — in one calm sequence."
            subtitle="The product is the story. Four steps, no clutter, clinician control at every stage."
          />
        </div>
        <ol className="hpv2-workflow-list">
          {steps.map((step, index) => (
            <li
              key={step.title}
              className={`hpv2-workflow-step hpv2-reveal hpv2-reveal-delay-${Math.min(index + 1, 3)}`}
            >
              <h3>{step.title}</h3>
              <p>{step.body}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

export function BenefitsSection() {
  const benefits = [
    {
      icon: BoltIcon,
      title: 'Faster drafting without shortcuts',
      body:
        'AI helps assemble a structured consent draft so clinicians spend time reviewing what matters — not retyping the same baseline content.',
    },
    {
      icon: DevicePhoneMobileIcon,
      title: 'Signing that fits real patients',
      body:
        'Send for signature digitally. Patients can complete consent away from the reception desk, on the device they already use.',
    },
    {
      icon: ShieldCheckIcon,
      title: 'Records you can return to',
      body:
        'Keep consent activity in one place so your team can follow the journey from draft to signature when questions arise.',
    },
  ];

  return (
    <section className="hpv2-section" id="features" aria-labelledby="hpv2-benefits-title">
      <div className="hpv2-container">
        <div className="hpv2-reveal">
          <SectionHead
            center
            eyebrow="Why practices choose SmileConsent"
            title="Three outcomes that matter in a busy surgery."
            subtitle="Speed, patient experience, and documentation — without turning the homepage into a feature catalogue."
          />
        </div>
        <div className="hpv2-benefits-grid">
          {benefits.map((item, index) => {
            const Icon = item.icon;
            return (
              <article
                key={item.title}
                className={`hpv2-benefit hpv2-reveal hpv2-reveal-delay-${index + 1}`}
              >
                <div className="hpv2-icon-well" aria-hidden="true">
                  <Icon className="h-6 w-6" />
                </div>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function AuditTrailSection() {
  return (
    <section className="hpv2-section" id="documentation" aria-labelledby="hpv2-audit-title">
      <div className="hpv2-container">
        <div className="hpv2-split">
          <div className="hpv2-split-copy hpv2-reveal">
            <SectionHead
              eyebrow="Documentation & audit trail"
              title="A consent journey you can reconstruct."
              subtitle="SmileConsent keeps key consent activity together — creation, sending, signing, and follow-up actions — so practices can support their own governance and preparation for scrutiny."
            />
            <ul className="hpv2-checklist">
              <li>
                <CheckCircleIcon aria-hidden="true" />
                <span>Timestamped activity on the consent pathway</span>
              </li>
              <li>
                <CheckCircleIcon aria-hidden="true" />
                <span>Digital signatures and PDF export for your records</span>
              </li>
              <li>
                <CheckCircleIcon aria-hidden="true" />
                <span>Clinician review sits before patient send — by design</span>
              </li>
            </ul>
          </div>
          <div className="hpv2-split-visual hpv2-reveal hpv2-reveal-delay-1">
            <div className="hpv2-visual-panel" aria-hidden="true">
              <div className="hpv2-mock-ui">
                <div className="hpv2-mock-row">
                  <div>
                    <strong>Consent created</strong>
                    <span>Draft ready for clinician review</span>
                  </div>
                  <span className="hpv2-pill hpv2-pill--blue">Logged</span>
                </div>
                <div className="hpv2-mock-row">
                  <div>
                    <strong>Sent to patient</strong>
                    <span>Secure link delivered by email</span>
                  </div>
                  <span className="hpv2-pill">Sent</span>
                </div>
                <div className="hpv2-mock-row">
                  <div>
                    <strong>Patient signed</strong>
                    <span>Signature captured · PDF available</span>
                  </div>
                  <span className="hpv2-pill">Complete</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function TreatmentIntelligenceSection() {
  return (
    <section className="hpv2-section" style={{ background: '#fff', borderBlock: '1px solid var(--hpv2-line)' }} id="treatment-intelligence">
      <div className="hpv2-container">
        <div className="hpv2-split hpv2-split--reverse">
          <div className="hpv2-split-copy hpv2-reveal">
            <SectionHead
              eyebrow="Treatment intelligence"
              title="Structured around real dental treatments — not a blank page."
              subtitle="SmileConsent helps assemble consent content around the treatments you select, including NHS and private context, so the draft starts closer to clinical reality."
            />
            <ul className="hpv2-checklist">
              <li>
                <CheckCircleIcon aria-hidden="true" />
                <span>Multiple treatments on a single consent where needed</span>
              </li>
              <li>
                <CheckCircleIcon aria-hidden="true" />
                <span>Costs included so the patient sees the full picture</span>
              </li>
              <li>
                <CheckCircleIcon aria-hidden="true" />
                <span>Editable draft — AI assists; clinicians decide</span>
              </li>
            </ul>
          </div>
          <div className="hpv2-split-visual hpv2-reveal hpv2-reveal-delay-1">
            <div className="hpv2-visual-panel">
              <img
                src={`${process.env.PUBLIC_URL || ''}/book-demo-hero.png`}
                alt="SmileConsent on a phone"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function GoogleReviewsSection() {
  return (
    <section className="hpv2-section" id="google-reviews" aria-labelledby="hpv2-reviews-feature-title">
      <div className="hpv2-container">
        <div className="hpv2-split">
          <div className="hpv2-split-copy hpv2-reveal">
            <SectionHead
              eyebrow="Google Reviews"
              title="Ask for a review when the moment is right — after consent is signed."
              subtitle="SmileConsent can send a branded review invitation after a signed consent, with a private feedback path for concerns that should not go public first."
            />
            <ul className="hpv2-checklist">
              <li>
                <EnvelopeIcon className="h-5 w-5" aria-hidden="true" style={{ color: 'var(--hpv2-teal)' }} />
                <span>One-click review requests from the consent record</span>
              </li>
              <li>
                <StarIcon className="h-5 w-5" aria-hidden="true" style={{ color: 'var(--hpv2-teal)' }} />
                <span>Public Google path for happy patients</span>
              </li>
              <li>
                <ShieldCheckIcon className="h-5 w-5" aria-hidden="true" style={{ color: 'var(--hpv2-teal)' }} />
                <span>Private practice feedback when something needs attention</span>
              </li>
            </ul>
            <div style={{ marginTop: '1.5rem' }}>
              <Link to="/dental-google-reviews" className="hpv2-btn hpv2-btn--ghost">
                Explore Google Reviews
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </div>
          <div className="hpv2-split-visual hpv2-reveal hpv2-reveal-delay-1">
            <div className="hpv2-visual-panel" aria-hidden="true">
              <div className="hpv2-mock-ui">
                <div className="hpv2-mock-row">
                  <div>
                    <strong>Consent signed</strong>
                    <span>Patient completed digital signature</span>
                  </div>
                  <span className="hpv2-pill">Ready</span>
                </div>
                <div className="hpv2-mock-row">
                  <div>
                    <strong>Review invitation</strong>
                    <span>Branded email · Google or private feedback</span>
                  </div>
                  <span className="hpv2-pill hpv2-pill--blue">Send</span>
                </div>
                <div className="hpv2-mock-row">
                  <div>
                    <strong>Engagement tracking</strong>
                    <span>Sent · opened · link clicked</span>
                  </div>
                  <span className="hpv2-pill">Visible</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function TestimonialsPlaceholder() {
  return (
    <section className="hpv2-section" id="testimonials" aria-labelledby="hpv2-testimonials-title">
      <div className="hpv2-container">
        <div className="hpv2-reveal">
          <SectionHead
            center
            eyebrow="Social proof"
            title="What practices say"
            subtitle="Real customer quotes belong here once approved. This mock-up intentionally leaves them blank."
          />
        </div>
        <div className="hpv2-placeholder-block hpv2-reveal hpv2-reveal-delay-1">
          <div className="hpv2-placeholder-tag">Needs approval</div>
          <h3 id="hpv2-testimonials-title">Testimonials placeholder</h3>
          <p>
            Do not invent quotes, clinic names, or star ratings. Supply approved testimonials
            (or verified Google review excerpts with permission) before this section goes live.
          </p>
        </div>
      </div>
    </section>
  );
}

export function SecuritySection() {
  const items = [
    {
      icon: LockClosedIcon,
      title: 'Access-controlled records',
      body: 'Clinic data stays behind authenticated practice access. Patient consent links use controlled access pathways.',
    },
    {
      icon: ShieldCheckIcon,
      title: 'GDPR-aware design',
      body: 'Built for UK practice use with privacy-minded handling of patient and consent information.',
    },
    {
      icon: DocumentTextIcon,
      title: 'Clinician accountability',
      body: 'AI assists drafting; your clinicians remain responsible for reviewing, editing, and approving what patients receive.',
    },
  ];

  return (
    <section className="hpv2-section hpv2-security" id="security" aria-labelledby="hpv2-security-title">
      <div className="hpv2-container">
        <div className="hpv2-reveal">
          <SectionHead
            center
            eyebrow="Security & privacy"
            title="Trust is part of the product, not a footnote."
            subtitle="Clear, honest posture: protect practice and patient information, keep clinicians in control, and avoid overclaiming certifications we have not listed here."
          />
        </div>
        <div className="hpv2-security-grid">
          {items.map((item, index) => {
            const Icon = item.icon;
            return (
              <article
                key={item.title}
                className={`hpv2-security-item hpv2-reveal hpv2-reveal-delay-${index + 1}`}
              >
                <div className="hpv2-icon-well" aria-hidden="true">
                  <Icon className="h-6 w-6" />
                </div>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function PricingSection() {
  return (
    <section className="hpv2-section hpv2-pricing" id="pricing" aria-labelledby="hpv2-pricing-title">
      <div className="hpv2-container">
        <div className="hpv2-reveal">
          <SectionHead
            center
            eyebrow="Pricing"
            title="Simple plans for UK practices."
            subtitle="Flat monthly pricing with clinician-friendly access — no per-seat maze. All plans include a 14-day free trial."
          />
        </div>
        <div className="hpv2-reveal hpv2-reveal-delay-1">
          <PricingComparisonCards />
        </div>
        <p className="hpv2-pricing-note">
          Cancel anytime · No long-term contracts · No setup fees · Secure payment via Stripe
        </p>
      </div>
    </section>
  );
}

export const HOMEPAGE_FAQS = [
  {
    q: 'Is the AI the final consent wording?',
    a: 'No. SmileConsent drafts a starting point. Clinicians review and edit before anything is sent to the patient. Responsibility for clinical accuracy and compliance stays with the practice.',
  },
  {
    q: 'How long is the free trial?',
    a: 'Every plan includes a 14-day free trial. Your card is not charged during the trial. Cancel anytime — no long-term contracts.',
  },
  {
    q: 'Does SmileConsent replace my practice management system?',
    a: 'No. SmileConsent focuses on the consent pathway — drafting, review, digital signing, records, and optional review requests — alongside your existing systems.',
  },
  {
    q: 'Can more than one clinician use the account?',
    a: 'Yes. Plans are designed around practice usage rather than charging per clinician seat. See the pricing cards above for consent volume and support differences.',
  },
  {
    q: 'What about Google Reviews?',
    a: 'After a patient signs consent, your team can send a branded review invitation. Happy patients can leave a Google review; concerns can be captured as private practice feedback first.',
  },
];

export function FaqSection() {
  const faqs = HOMEPAGE_FAQS;

  return (
    <section className="hpv2-section" id="faq" aria-labelledby="hpv2-faq-title">
      <div className="hpv2-container">
        <div className="hpv2-reveal">
          <SectionHead
            center
            eyebrow="FAQ"
            title="Straight answers before you start."
            subtitle="The questions practices ask most often when evaluating SmileConsent."
          />
        </div>
        <div className="hpv2-faq hpv2-reveal hpv2-reveal-delay-1">
          {faqs.map((item) => (
            <details key={item.q}>
              <summary>
                {item.q}
                <span aria-hidden="true">
                  <PlusIcon className="h-4 w-4" />
                </span>
              </summary>
              <p>{item.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

export function FinalCtaSection() {
  return (
    <section className="hpv2-section hpv2-final-cta" aria-labelledby="hpv2-final-cta-title">
      <div className="hpv2-container hpv2-reveal">
        <div className="hpv2-eyebrow" style={{ color: '#99f6e4', justifyContent: 'center' }}>
          <SparklesIcon className="h-4 w-4" aria-hidden="true" />
          Ready when you are
        </div>
        <h2 id="hpv2-final-cta-title">Give your practice a clearer consent pathway.</h2>
        <p>
          Start a free trial, walk through a demo, or explore the product video above.
          Keep clinicians in control — and keep the record ready when you need it.
        </p>
        <div className="hpv2-final-actions">
          <Link to="/register" className="hpv2-btn hpv2-btn--light">
            Start free trial
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
          </Link>
          <Link to="/book-demo" className="hpv2-btn hpv2-btn--outline-light">
            Book a demo
          </Link>
        </div>
      </div>
    </section>
  );
}

const SOCIAL_LINKS = [
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/smileconsentuk/',
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path
          fill="currentColor"
          d="M7 3h10a4 4 0 0 1 4 4v10a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V7a4 4 0 0 1 4-4zm10 2H7a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2zm-5 3.2A3.8 3.8 0 1 1 8.2 12 3.8 3.8 0 0 1 12 8.2zm0 2A1.8 1.8 0 1 0 13.8 12 1.8 1.8 0 0 0 12 10.2zM17.4 6.6a1 1 0 1 1-1 1 1 1 0 0 1 1-1z"
        />
      </svg>
    ),
  },
  {
    label: 'TikTok',
    href: 'https://www.tiktok.com/@smileconsentuk',
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path
          fill="currentColor"
          d="M14.5 3c.4 2.4 1.8 4.1 4.1 4.4v2.5a7.2 7.2 0 0 1-4.1-1.3v6.6a5.7 5.7 0 1 1-5.7-5.7c.3 0 .6 0 .9.1v2.6a3.1 3.1 0 1 0 2.2 3V3h2.6z"
        />
      </svg>
    ),
  },
  {
    label: 'Facebook',
    href: 'https://www.facebook.com/profile.php?id=61579659454431',
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path
          fill="currentColor"
          d="M14.2 21v-7.2h2.4l.4-2.8h-2.8V9.2c0-.8.2-1.4 1.4-1.4H17V5.2A18 18 0 0 0 14.8 5c-2.2 0-3.7 1.3-3.7 3.8v2H8.6v2.8h2.5V21h3.1z"
        />
      </svg>
    ),
  },
  {
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/in/smile-consent-844399381/',
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path
          fill="currentColor"
          d="M6.5 9.2H3.7V20h2.8V9.2zM5.1 3.8A1.7 1.7 0 1 0 5.1 7a1.7 1.7 0 0 0 0-3.2zM20.3 20h-2.8v-5.6c0-1.5-.5-2.5-1.8-2.5a2 2 0 0 0-1.8 1.3 2.4 2.4 0 0 0-.1.9V20h-2.8s.1-9.3 0-10.8h2.8v1.8a2.8 2.8 0 0 1 2.5-1.4c1.8 0 3.2 1.2 3.2 3.8V20z"
        />
      </svg>
    ),
  },
];

export function FooterSection() {
  return (
    <footer className="hpv2-footer">
      <div className="hpv2-container">
        <div className="hpv2-footer-grid">
          <div>
            <div className="hpv2-brand" style={{ pointerEvents: 'none' }}>
              <span className="hpv2-brand-mark" aria-hidden="true">
                <DocumentCheckIcon className="h-5 w-5" />
              </span>
              <span className="hpv2-brand-name">SmileConsent</span>
            </div>
            <p>
              AI-assisted dental consent for UK practices — draft, review, send, and keep a clear record.
            </p>
            <ul className="hpv2-social">
              {SOCIAL_LINKS.map((item) => (
                <li key={item.label}>
                  <a href={item.href} target="_blank" rel="noopener noreferrer" aria-label={item.label}>
                    {item.icon}
                    <span>{item.label}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4>Product</h4>
            <ul>
              <li><a href="#how-it-works">How it works</a></li>
              <li><a href="#features">Features</a></li>
              <li><a href="#pricing">Pricing</a></li>
              <li><Link to="/dental-google-reviews">Google Reviews</Link></li>
            </ul>
          </div>
          <div>
            <h4>Company</h4>
            <ul>
              <li><Link to="/contact">Contact</Link></li>
              <li><Link to="/book-demo">Book a demo</Link></li>
              <li><Link to="/support">Support</Link></li>
              <li><Link to="/dental-consent-forms">Consent forms</Link></li>
            </ul>
          </div>
          <div>
            <h4>Legal</h4>
            <ul>
              <li><Link to="/privacy">Privacy</Link></li>
              <li><Link to="/terms">Terms</Link></li>
            </ul>
          </div>
        </div>
        <div className="hpv2-footer-bottom">
          <span>© {new Date().getFullYear()} {COMPANY.legalName}. All rights reserved.</span>
          <span>Built for UK dental practices</span>
        </div>
        <div className="hpv2-company-line">
          <CompanyDisclosure tone="dark" showCopyright={false} />
        </div>
      </div>
    </footer>
  );
}
